// Animated Digital Nameplate — used on public store hero.
// Zero-cost VFX via CSS keyframes + optional Google Font.
import React, { useEffect } from 'react';

const THEMES = {
  neon:      { bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', color: '#fbbf24', shadow: '#fbbf24' },
  gold:      { bg: 'linear-gradient(135deg, #422006 0%, #78350f 50%, #422006 100%)', color: '#fef3c7', shadow: '#f59e0b' },
  neon_blue: { bg: 'linear-gradient(135deg, #0c1420 0%, #172554 50%, #0c1420 100%)', color: '#7dd3fc', shadow: '#0284c7' },
  sunset:    { bg: 'linear-gradient(135deg, #7c2d12 0%, #b91c1c 50%, #7e22ce 100%)', color: '#fff', shadow: '#f43f5e' },
  emerald:   { bg: 'linear-gradient(135deg, #022c22 0%, #064e3b 50%, #022c22 100%)', color: '#6ee7b7', shadow: '#10b981' },
  fire:      { bg: 'linear-gradient(135deg, #450a0a 0%, #991b1b 50%, #450a0a 100%)', color: '#fed7aa', shadow: '#ea580c' },
  cosmic:    { bg: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #831843 100%)', color: '#e9d5ff', shadow: '#c026d3' },
  royal:     { bg: 'linear-gradient(135deg, #1e3a8a 0%, #6b21a8 50%, #1e3a8a 100%)', color: '#fde68a', shadow: '#a855f7' },
};

const FONT_MAP = {
  'Playfair Display': 'Playfair+Display:wght@700;900',
  'Bebas Neue': 'Bebas+Neue',
  'Pacifico': 'Pacifico',
  'Orbitron': 'Orbitron:wght@700;900',
  'Righteous': 'Righteous',
  'Cinzel': 'Cinzel:wght@700;900',
  'Anton': 'Anton',
  'Dancing Script': 'Dancing+Script:wght@700',
};

export default function StoreNameplate({ nameplate = {}, shopName, verified, sponsored, tagline }) {
  const theme = THEMES[nameplate.theme] || THEMES.neon;
  const font = nameplate.font_family || 'Playfair Display';
  const vfx = nameplate.vfx_style || 'glow';
  const glow = nameplate.glow_color || theme.shadow;
  const useTagline = nameplate.tagline || tagline;

  useEffect(() => {
    const gFont = FONT_MAP[font];
    if (!gFont) return;
    const id = `gfont-${font.replace(/\s/g, '')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id; link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${gFont}&display=swap`;
    document.head.appendChild(link);
  }, [font]);

  const nameStyle = {
    fontFamily: `'${font}', serif`,
    color: theme.color,
    textShadow: vfx === 'glow' ? `0 0 20px ${glow}, 0 0 40px ${glow}, 0 0 60px ${glow}` :
                vfx === 'pulse' ? `0 0 15px ${glow}` :
                vfx === 'sparkle' ? `0 0 8px ${glow}, 0 0 16px ${glow}` : 'none',
    animation: vfx === 'pulse' ? 'nameplate-pulse 2s ease-in-out infinite' :
               vfx === 'sparkle' ? 'nameplate-sparkle 1.5s ease-in-out infinite' :
               vfx === 'marquee' ? 'nameplate-marquee 12s linear infinite' : 'none',
  };

  return (
    <div className="relative overflow-hidden rounded-2xl p-6 sm:p-10 text-center" data-testid="store-nameplate"
         style={{ background: theme.bg, minHeight: '160px' }}>
      {/* Ambient VFX layers */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{
             background: `radial-gradient(circle at 20% 30%, ${glow}33 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${glow}22 0%, transparent 40%)`,
             animation: vfx === 'sparkle' ? 'nameplate-shimmer 3s ease-in-out infinite' : 'none',
           }} />
      <div className="relative">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <h1 className="text-3xl sm:text-5xl font-black tracking-wide" style={nameStyle} data-testid="nameplate-shop-name">
            {shopName}
          </h1>
          {verified && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-black">✓ Verified</span>}
          {sponsored && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black">★ Sponsored</span>}
        </div>
        {useTagline && (
          <div className="mt-3 text-sm sm:text-lg opacity-90 italic" style={{ color: theme.color, fontFamily: `'${font}', serif` }} data-testid="nameplate-tagline">
            {useTagline}
          </div>
        )}
      </div>
      <style>{`
        @keyframes nameplate-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.03); opacity: 0.9; } }
        @keyframes nameplate-sparkle { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.4) saturate(1.5); } }
        @keyframes nameplate-shimmer { 0%,100% { opacity: 0.4; } 50% { opacity: 0.7; } }
        @keyframes nameplate-marquee { 0% { transform: translateX(0); } 50% { transform: translateX(-5%); } 100% { transform: translateX(0); } }
      `}</style>
    </div>
  );
}

export const NAMEPLATE_THEMES = Object.keys(THEMES);
export const NAMEPLATE_FONTS = Object.keys(FONT_MAP);
export const NAMEPLATE_VFX = ['pulse', 'glow', 'sparkle', 'marquee', 'static'];
